/*
 * Copyright (c) 2024 SARL SIGMA POLYNESIA
 */

import { useCallback, useMemo } from 'react'
import {
  deleteTransactionFileById,
  downloadTransactionFileById,
  findManyTransactionFiles,
  uploadTransactionFile
} from '../../lib/rest/transactionFileAPIClient'
import FileInput from '../inputs/FileInput'
import { useQuery } from '@tanstack/react-query'
import { getHalEmbedded } from '@jalik/hal'
import { useSettingsContext } from '../SettingsProvider'

export type TransactionFileFieldProps = {
  transactionId?: number,
  tmpRef: string
}

function TransactionFileField (props: TransactionFileFieldProps) {
  const { transactionId, tmpRef, ...others } = props

  const { settings } = useSettingsContext()

  const filters = useMemo(() => (
    transactionId > 0
      ? { transactionId }
      : { tmpRef }
  ), [transactionId, tmpRef])

  const loader = useQuery({
    enabled: filters != null,
    queryKey: ['transactionFile', filters],
    queryFn: () => findManyTransactionFiles(filters)
      .then((json) => (getHalEmbedded(json)?.files || [])[0] || {} as Partial<TransactionFile>),
    refetchOnWindowFocus: false
  })
  const { data: file } = loader
  const fileId = file?.id

  const downloadFunction = useCallback(() => downloadTransactionFileById(fileId), [fileId])
  const deleteFunction = useCallback(() => deleteTransactionFileById(fileId), [fileId])
  const uploadFunction = useCallback((file1: File) => uploadTransactionFile(file1, tmpRef, transactionId), [transactionId, tmpRef])

  return (
    <FileInput
      {...others}
      accept={settings.TRANSACTION_FILE_TYPES}
      maxSize={settings.TRANSACTION_MAX_UPLOAD_SIZE}
      name="transaction_file"
      onDownload={downloadFunction}
      onDelete={deleteFunction}
      onChange={() => loader.refetch()}
      onUpload={uploadFunction}
      value={file?.name}
      lastModified={file?.createdAt}
      contentLength={file?.size}
    />
  )
}

export default TransactionFileField
